import { type CardType, TOOLBAR_CARDS } from "@mn/shared";
import { useT } from "@/lib/i18n";
import { cardIcon } from "./icons";
import { useBoardUI } from "./store";

const ENABLED: CardType[] = [
  "note",
  "heading",
  "image",
  "link",
  "board",
  "column",
  "todo",
  "comment",
  "line",
  "swatch",
  "sketch",
  "annotation",
  "document",
  "table",
  "video",
  "audio",
  "file",
];
const UPLOAD: CardType[] = ["image", "file", "video", "audio"];

export function Toolbar({
  onPick,
  onUpload,
  onConnect,
}: {
  onPick: (type: CardType) => void;
  onUpload: (kind: CardType) => void;
  onConnect: () => void;
}) {
  const t = useT();
  const tool = useBoardUI((s) => s.tool);
  const setTool = useBoardUI((s) => s.setTool);
  return (
    <div className="absolute z-20 flex gap-1 border-border bg-panel p-1.5 shadow-panel max-md:inset-x-0 max-md:bottom-0 max-md:flex-row max-md:overflow-x-auto max-md:border-t max-md:pb-[max(0.375rem,env(safe-area-inset-bottom))] md:left-3 md:top-1/2 md:-translate-y-1/2 md:flex-col md:rounded-md md:border">
      <ToolBtn
        label={t.tools.select ?? "Select"}
        active={tool === "select"}
        onClick={() => setTool("select")}
      >
        {cardIcon("select")}
      </ToolBtn>
      <ToolBtn label={t.tools.hand ?? "Hand"} active={tool === "hand"} onClick={() => setTool("hand")}>
        {cardIcon("hand")}
      </ToolBtn>
      <div className="border-border max-md:mx-1 max-md:border-l md:my-1 md:border-t" />
      <div className="flex gap-0.5 md:grid md:grid-cols-2">
        {TOOLBAR_CARDS.filter((c) => ENABLED.includes(c.type)).map((c) => (
          <ToolBtn
            key={c.type}
            label={c.type === "line" ? (t.connectHint ?? "") : (t.cardTypes[c.type] ?? c.type)}
            onClick={() =>
              UPLOAD.includes(c.type) ? onUpload(c.type) : c.type === "line" ? onConnect() : onPick(c.type)
            }
          >
            {cardIcon(c.type) ?? c.icon}
          </ToolBtn>
        ))}
      </div>
    </div>
  );
}

function ToolBtn({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={`flex size-9 shrink-0 items-center justify-center rounded-sm text-base transition ${active ? "bg-accent text-bg" : "text-muted hover:bg-panel-2 hover:text-text"}`}
    >
      {children}
    </button>
  );
}
